import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';

/**
 * The frame every work screen sits in.
 *
 * A page used to be a document: `space-y-6` growing downwards until the browser
 * scrolled it. That is the right shape for an article and the wrong one for a
 * screen somebody works in all day — the totals slide off the bottom while you
 * are still typing at the top, and you lose the list you were comparing against.
 *
 * Screen pins the page to the viewport. Whatever needs to scroll scrolls inside
 * its own pane, so the header, the facts and the totals never move.
 *
 * Below lg it stops pinning. A phone has no room to split: the panes that sit
 * side by side on a desktop stack, and three stacked panes sharing one viewport
 * height are three postage-stamp scroll boxes. Letting the page grow gives the
 * phone the one scroll it expects instead.
 */
export function Screen({ children, className, ...props }) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-col gap-2.5',
        'min-h-[calc(100dvh-var(--shell-offset))] lg:h-[calc(100dvh-var(--shell-offset))] lg:min-h-0',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

/**
 * The main pane and an optional rail beside it. Both scroll on their own; the
 * rail folds under the main pane on narrow screens rather than squeezing it.
 */
export function ScreenSplit({ children, rail, railWidth = '20rem', className }) {
  if (!rail) {
    return <div className={cn('flex min-h-0 min-w-0 flex-1 flex-col', className)}>{children}</div>;
  }
  return (
    <div
      className={cn('grid min-h-0 flex-1 gap-2.5 lg:grid-cols-[minmax(0,1fr)_var(--rail-w)]', className)}
      style={{ '--rail-w': railWidth }}
    >
      <div className="flex min-h-0 min-w-0 flex-col">{children}</div>
      <div className="flex min-h-0 min-w-0 flex-col gap-2.5 overflow-y-auto">{rail}</div>
    </div>
  );
}

/**
 * A titled region with its own scroll.
 *
 * Replaces `<Card><CardHeader><CardTitle>` on work screens. A Card spends about
 * 26px of padding and a shadow on every group; a Pane spends a 26px title bar
 * that also carries the group's count and its actions. Same chrome, more of it
 * doing a job.
 */
export function Pane({
  title,
  aside,
  actions,
  children,
  className,
  bodyClassName,
  scroll = true,
  collapsible = false,
  defaultOpen = true,
  tone = 'blue',
}) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = collapsible ? open : true;

  const headTone = tone === 'plain'
    ? 'bg-slate-100 text-slate-600 border-b border-slate-200'
    : 'bg-brand-blue text-white';

  const Head = collapsible ? 'button' : 'div';

  return (
    <section
      className={cn(
        'flex min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-card',
        !isOpen && 'flex-none',
        className
      )}
    >
      <Head
        type={collapsible ? 'button' : undefined}
        onClick={collapsible ? () => setOpen(o => !o) : undefined}
        aria-expanded={collapsible ? isOpen : undefined}
        className={cn(
          'flex shrink-0 items-center justify-between gap-2 px-3 py-2.5 text-left sm:py-1.5',
          headTone,
          collapsible && 'hover:brightness-110 transition-[filter]'
        )}
      >
        <span className="flex min-w-0 items-center gap-2">
          {collapsible && (
            <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 transition-transform', !isOpen && '-rotate-90')} />
          )}
          <span className="truncate text-[11px] font-semibold uppercase tracking-wider">{title}</span>
          {aside && (
            <span className={cn('truncate text-[10px] font-normal normal-case tracking-normal',
              tone === 'plain' ? 'text-slate-400' : 'text-blue-100')}>
              {aside}
            </span>
          )}
        </span>
        {actions && <span className="flex shrink-0 items-center gap-1">{actions}</span>}
      </Head>

      {isOpen && (
        <div className={cn('min-h-0 flex-1', scroll && 'overflow-y-auto', bodyClassName)}>
          {children}
        </div>
      )}
    </section>
  );
}

export default Screen;
